import { Injectable } from '@angular/core';
import { FormsService } from '../forms.service';
import { JobsService } from '../../jobs/jobs.service';
import { MessageService } from 'primeng/api';
import { ConfirmationService } from 'primeng/api';
import { NotesService } from '../../notes/notes.service';
import { Loading } from 'notiflix';
import { Job } from '../../jobs/job';
import { ISqlJob } from '../../jobs/sql-job.interface';
import { JOB_STATUS } from '../../jobs/job-status.enum';
import { FormioComponent } from '@formio/angular';
import { formatDate } from '../../utils/format-date';
@Injectable({
  providedIn: 'root'
})
export class InspectionFormService {

  constructor(
    private formsService: FormsService,
    private jobsService: JobsService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private notesService: NotesService
  ) {}

  async initializeFormData(scJob: ISqlJob): Promise<{ form: any; formData: any; job: Job; viewOnly: boolean }> {
    const jobDetails = await this.jobsService.getJobDetails(scJob.scJobId);
    const viewOnly = jobDetails.jobRequest.JobStatusId === JOB_STATUS.DELIVERED || 
                    jobDetails.jobRequest.JobStatusId === JOB_STATUS.READY_TO_DELIVER;

    const form = await this.formsService.getFormById(scJob.formId);
    if (!form || !('inspectionForm' in form) || typeof form.inspectionForm !== 'string') {
      throw new Error('Invalid form data received');
    }

    const formDef = JSON.parse(form.inspectionForm);
    const job = await this.jobsService.getJobById(scJob.jobId);

    let formData;
    if (job?.inspectionData) {
      formData = {
        data: {
          ...scJob,
          tagFormTypeId: scJob.scFormTypeId,
          tagRefNum: scJob.scJobId,
          tagUid: scJob.scRepUserId,
          ...job.inspectionData,
          scSpecialInstructions: jobDetails.insurance.specialinstructions,
        }
      };
    } else {
      formData = {
        data: scJob
      };
    }

    return { form: formDef, formData, job, viewOnly };
  }

  async saveInspectionForm(jobId: string, editTimestamp: Date|null = null, formComponent: FormioComponent): Promise<{updated: boolean, job: Job}> {
    Loading.standard('Saving Form Progress...');
    try {
      if (!editTimestamp) {
        throw new Error('Data edit timestamp is required');
      }
      const { updated, job: updatedJob, jobUpdatedBy, jobUpdatedAt } = await this.jobsService.updateJobIfNewer(jobId, editTimestamp, { inspectionData: formComponent.submission.data });
      if (!updated) {
        Loading.remove();
        const saveAnyway = await new Promise<boolean>((resolve) => {
          this.confirmationService.confirm({
            key: 'formConfirmDialog',
            message: `The form was not saved because the data might have been updated by ${jobUpdatedBy ? jobUpdatedBy : 'someome else'}${jobUpdatedAt ? ' on ' + formatDate(jobUpdatedAt) : ''}. Do you want to save anyway? This will overwrite any existing changes.`,
            accept: () => resolve(true),
            reject: () => resolve(false)
          });
        });
        if (saveAnyway) {
          // const newSubmission = {
          //   data: {   
          //     ...formComponent.submission.data,
          //     ...updatedJob.inspectionData
          //   }
          // };
          // formComponent.formio.submission = newSubmission;
          // formComponent.formio.emit('change', newSubmission);
          // editTimestamp = new Date();
          try {
            await this.jobsService.updateJob(jobId, { inspectionData: formComponent.submission.data });
            this.messageService.add({
              severity: 'success',
              summary: 'Form Saved',
              detail: 'The latestest changes have been saved.'
            });
            editTimestamp.setTime(Date.now());
            return { updated: true, job: updatedJob };
          } catch (error) {
            console.error('Unable to save the form:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Unable to Save Form Progress',
              detail: 'Form progress was not saved. Please try again or contact your coordinator for help.'
            });
            return { updated: false, job: updatedJob };
          }
        }
        return { updated: false, job: updatedJob };
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Save Successful',
        detail: 'Form progress has been saved successfully.'
      });

      editTimestamp.setTime(Date.now());
      return { updated: true, job: updatedJob };

    } catch (error) {
      console.error('Unable to save the form:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Unable to Save Form Progress',
        detail: 'Form progress was not saved. Please try again or contact your coordinator for help.'
      });
      throw error;
    } finally {
      Loading.remove();
    }
  }

  async submitToQc(jobId: string, editTimestamp: Date|null = null, formComponent: FormioComponent = {} as FormioComponent): Promise<Job|undefined> {
    Loading.standard('Saving and Submitting for QC Review...');
    try {

      const job = await this.jobsService.getJobById(jobId);
      if (!job) {
        throw new Error(`Unable to find job to submit to QC`);
      }

      const validation = await this.jobsService.validateJobForQc(
        jobId, 
        formComponent?.formio?.submission?.data || job.inspectionData
      );
      
      if (!validation.isReadyForQc) {
        this.messageService.add({
          severity: 'error',
          summary: 'QC Submission Failed',
          detail: 'Please ensure all required fields are filled out correctly and you have uploaded your photos (if required for this inspection).'
        });
        return;
      }

      const updatedJob = await this.jobsService.updateJob(jobId, { status: 'QC' });
      if (!updatedJob) {
        throw new Error('Unable to update job status to QC');
      }
      const updatedISqlJob = await this.jobsService.updateJobStatusId(updatedJob.scJobId, JOB_STATUS.QC );
      console.log('updatedISqlJob', updatedISqlJob);
      
      await this.jobsService.postFormDataLegacy(jobId);

      this.messageService.add({
        severity: 'success',
        summary: 'Submitted to QC',
        detail: 'The form has been submitted successfully for QC review.'
      });

      await this.notesService.createScJobNote(
        job.scJobId,
        'Inspector has completed the data entry process and submitted the finalized report. The status was updated to QC.',
        true
      );
      return updatedJob;
    } catch (error) {
      console.error('Unable to submit to QC:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Unable to Submit to QC',
        detail: 'The form was not submitted. Please try again or contact your coordinator for help.'
      });
      throw error;
    } finally {
      Loading.remove();
    }
  }

  async confirmAction(message: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        key: 'formConfirmDialog',
        message,
        accept: () => resolve(true),
        reject: () => resolve(false)
      });
    });
  }
  
  async acknowledgeErrors(errors: any[]): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        key: 'formConfirmDialog',
        header: 'Unable to Submit to QC',
        message: `Please review the ${errors.length} validation ${errors.length === 1 ? 'error' : 'errors'} in the form and fix them before submitting to QC.`,
        rejectVisible: false,
        acceptLabel: 'Return to Form',
        defaultFocus: 'accept',
        accept: () => resolve(true)
      });
    });
  }

  async validateForm(jobId: string, formData: any): Promise<{ isValid: boolean; errors: any[] }> {
    try {
      const validation = await this.jobsService.validateJobForQc(jobId, formData);
      return {
        isValid: validation.isReadyForQc,
        errors: validation.errors
      };
    } catch (error) {
      console.error('Error validating form:', error);
      return {
        isValid: false,
        errors: [error]
      };
    }
  }
}
